/**
 * Active-workspace role lookup usable OUTSIDE React (the authProvider runs in
 * plain functions). Mirrors the membership model used by useWorkspaces.
 *
 * SECURITY NOTE: this is UI gating only. RLS enforces real permissions on
 * every query regardless of what this returns.
 */
import { getSupabaseClient } from "../../atomic-crm/providers/supabase/supabase";
import { getActiveWorkspaceId } from "./activeWorkspace";

/** Role keys treated as admin-level for client-side gating (matches the
 * server-side check in the platform RLS helpers). */
const ADMIN_ROLE_KEYS = new Set(["workspace_owner", "administrator"]);

let cache: { key: string; roleKey: string | null; at: number } | null = null;
const TTL_MS = 60_000;

export const getActiveWorkspaceRoleKey = async (): Promise<string | null> => {
  const supabase = getSupabaseClient();
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return null;

  const workspaceId = getActiveWorkspaceId();
  const cacheKey = `${user.id}:${workspaceId ?? "default"}`;
  if (cache && cache.key === cacheKey && Date.now() - cache.at < TTL_MS) {
    return cache.roleKey;
  }

  let query = supabase
    .from("workspace_members")
    .select("workspace_id, roles(key)")
    .eq("user_id", user.id);
  if (workspaceId) {
    query = query.eq("workspace_id", workspaceId);
  }
  const { data, error } = await query.limit(1);
  if (error) return null;

  const roleKey =
    ((data?.[0] as { roles?: { key?: string } | null } | undefined)?.roles
      ?.key as string | undefined) ?? null;
  cache = { key: cacheKey, roleKey, at: Date.now() };
  return roleKey;
};

export const isAdminRoleKey = (roleKey: string | null): boolean =>
  roleKey != null && ADMIN_ROLE_KEYS.has(roleKey);

export const clearActiveWorkspaceRoleCache = () => {
  cache = null;
};
